/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Modal, Row } from "react-bootstrap";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import { useAsyncDebounce } from "react-table";
import { Controller, useForm } from "react-hook-form";
import ReactSelect from "react-select";
import { api, ApiError } from "core/api";
import { Alert, Button, Card, ControlledTable, Input, Select, SvgIcon, Table } from "core/components";
import formatter from "core/formatter";
import { navigateTo } from "core/navigation";
import moment from "moment";
import {
  admin_purchase_orders_path,
  admin_purchase_requests_path,
  admin_purchase_request_path,
  approve_purchase_request_admin_project_path,
  delete_purchase_request_admin_project_path,
  print_admin_purchase_request_path,
  reject_purchase_request_admin_project_path
} from "routes";
import ProjectPurchaseRequest from "./ProjectPurchaseRequest";
import PurchaseRequestHistory from "./PurchaseRequestHistory";
import { PurchaseRequestStatus } from "core/schema";
import ControlledTableContext from "core/components/ControlledTableContext";

type PurchaseRequestsProps = {
  has_purchase_request_items: boolean;
  has_purchase_request_history: boolean;
};

const PurchaseRequests = ({ has_purchase_request_items, warehouses, has_purchase_request_history }: PurchaseRequestsProps): JSX.Element => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [purchaseRequestItems, setPurchaseRequestItems] = React.useState([]);
  const [selectedItems, setSelectedItems] = React.useState([]);
  const [pagination, setPagination] = React.useState(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [tabIndex, setTabIndex] = React.useState(0);
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    watch,
    formState: { isSubmitting, isValidating }
  } = useForm({
    mode: "onChange"
  });
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);
  const table = React.useRef(null);
  const [showPurchaseOrdersModal, setShowPurchaseOrdersModal] = React.useState(false);
  const [selectedPurchaseRequest, setSelectedPurchaseRequest] = React.useState(null);
  const [purchaseOrders, setPurchaseOrders] = React.useState([]);
  const [warehouseFilter, setWarehouseFilter] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("");
  const isForUser = window.location.href.includes("project_purchase_requests");
  const [purchaseRequests, setPurchaseRequests] = React.useState([]);
  const [purchaseRequest, setPurchaseRequest] = React.useState(null);
  const [statuses, setStatuses] = React.useState([]);
  const [showPurchaseRequestModal, setShowPurchaseRequestModal] = React.useState(false);
  const [showApprovePurchaseRequestModal, setShowApprovePurchaseRequestModal] = React.useState(false);
  const [showRejectPurchaseRequestModal, setShowRejectPurchaseRequestModal] = React.useState(false);
  const [showDeletePurchaseRequestModal, setShowDeletePurchaseRequestModal] = React.useState(false);
  const [itemsForDestruction, setItemsForDestruction] = React.useState([]);
  const [hasPurchaseRequestHistory, setHasPurchaseRequestHistory] = React.useState(has_purchase_request_history);
  const abortController = React.useRef<AbortController>();

  const aggregateValue = (leafValues) => {
    return leafValues[0];
  };

  const aggregateSum = (leafValues) => {
    const reducer = (accumulator, currrent) => parseFloat(accumulator) + parseFloat(currrent);

    return leafValues.reduce(reducer);
  };

  const save = async (_data) => {
    const purchaseRequests = selectedItems;

    try {
      if (purchaseRequests.length > 0) {
        await api.post(admin_purchase_orders_path(), {
          purchase_requests: purchaseRequests
        });
        navigateTo(admin_purchase_orders_path());
      }
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  const onError = (data) => {
    console.error(data);
    if (data?.data) {
      setFormErrors(["There are errors on your form."]);
    }
  };

  const addToSelectedItems = (item) => {
    setSelectedItems((items) => [...items, item]);
  };

  const removeFromSelectedItems = (item) => {
    setSelectedItems((selectedItems) => selectedItems.filter((selectedItem) => selectedItem.id != item.id));
  };

  let columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id",
        Cell: () => {
          return <></>;
        }
      },
      {
        id: "purchase_request_code",
        Header: "Purchase Request",
        accessor: "purchase_request.code"
      },
      {
        id: "item_code",
        Header: "Inventory ID",
        accessor: "item.code",
        style: {
          width: "10%"
        },
        Cell: (props) => {
          return (
            <div title={`${props.value} (${props.row.subRows.length})`} data-bs-toggle="tooltip">
              <p className="text-center text-bold text-truncate">{`${props.value} (${props.row.subRows.length})`}</p>
            </div>
          );
        }
      },
      {
        id: "item_name",
        Header: "Inventory",
        accessor: "item.name",
        style: {
          width: "13%"
        },
        aggregate: aggregateValue,
        Aggregated: ({ value }) => (
          <div title={value} data-bs-toggle="tooltip">
            <p className="text-truncate text-bold">{value}</p>
          </div>
        ),
        Cell: (props) => {
          const project = props.row.original.project;
          const subdivision = props.row.original.subdivision;
          const warehouse = props.row.original.warehouse;

          return (
            <>
              <p className="text-truncate" title={project.code} data-bs-toggle="tooltip">
                {project.code}
              </p>
              <p className="text-truncate" title={subdivision?.name} data-bs-toggle="tooltip">
                {subdivision?.name}
              </p>
              <p className="text-truncate" title={warehouse?.name} data-bs-toggle="tooltip">
                {warehouse?.name}
              </p>
            </>
          );
        }
      },
      {
        id: "project_code",
        Header: "Project ID",
        accessor: "project.code",
        Cell: () => {
          return <></>;
        }
      },
      {
        id: "subdivision",
        Header: "Subdivision",
        accessor: "subdivision.name",
        Cell: () => {
          return <></>;
        }
      },
      {
        id: "warehouse",
        Header: "Warehouse",
        accessor: "warehouse.id",
        Cell: () => {
          return <></>;
        }
      },
      {
        Header: "P.R Info",
        accessor: "purchase_request.code",
        Cell: (props) => {
          const purchaseRequest = props.row.original?.purchase_request;

          return (
            <>
              <p className="text-center text-truncate" title={props.value} data-bs-toggle="tooltip">
                {props.value}
              </p>
              <p className="text-center text-truncate" title={purchaseRequest?.created_by} data-bs-toggle="tooltip">
                {purchaseRequest?.created_by}
              </p>
              <p className="text-center text-truncate" title={props.row.original?.remarks} data-bs-toggle="tooltip">
                {props.row.original?.remarks}
              </p>
            </>
          );
        }
      },
      {
        Header: "Requested Quantity",
        accessor: "requested_quantity",
        aggregate: aggregateSum,
        Aggregated: ({ value }) => (
          <div title={value} data-bs-toggle="tooltip">
            <p className="text-center text-bold">{value}</p>
          </div>
        ),
        style: {
          textAlign: "center"
        },
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Ordered Quantity",
        accessor: "ordered_quantity",
        aggregate: aggregateSum,
        Aggregated: ({ value }) => (
          <div title={value} data-bs-toggle="tooltip">
            <p className="text-center text-bold">{value}</p>
          </div>
        ),
        style: {
          textAlign: "center"
        },
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Remaining Quantity",
        accessor: "remaining_quantity",
        aggregate: aggregateSum,
        Aggregated: ({ value }) => (
          <div title={value} data-bs-toggle="tooltip">
            <p className="text-center text-bold">{value}</p>
          </div>
        ),
        style: {
          textAlign: "center"
        },
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Delivered Quantity",
        accessor: "delivered_quantity",
        aggregate: aggregateSum,
        Aggregated: ({ value }) => (
          <div title={value} data-bs-toggle="tooltip">
            <p className="text-center text-bold">{value}</p>
          </div>
        ),
        style: {
          textAlign: "center"
        },
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "UOM",
        accessor: "unit_measure_code",
        aggregate: aggregateValue,
        Aggregated: ({ value }) => <p className="text-center text-bold">{value}</p>,
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        Header: "Date Needed",
        accessor: "date_needed",
        Cell: (props) => {
          return props.value ? (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{moment(props.value).format("MM/DD/YYYY")}</p>
            </div>
          ) : (
            <></>
          );
        }
      },
      {
        Header: "Supplier",
        accessor: "",
        style: {
          width: "17%"
        },
        Cell: (props) => {
          const isAggregate = props.row.canExpand;
          const purchaseRequestItem = props.row.original;
          const itemSuppliers = props.row.original?.item_suppliers || [];

          return isAggregate ? (
            ""
          ) : (
            <Row>
              <Col>
                <Controller
                  name={`data.${props.row.index}.item_supplier_id`}
                  control={control}
                  render={({ field }) => (
                    <ReactSelect
                      isSearchable
                      isClearable
                      options={itemSuppliers}
                      placeholder="Select a Supplier"
                      noOptionsMessage={() => "No available suppliers."}
                      getOptionLabel={(option) => option.supplier.name}
                      getOptionValue={(option) => option.id}
                      onChange={(itemSupplier, actionMeta) => {
                        field.onChange(itemSupplier?.id);

                        const unitPrice = parseFloat(itemSupplier?.current_price);
                        const requestedQuantity = parseFloat(purchaseRequestItem.requested_quantity);
                        const totalAmount = requestedQuantity * unitPrice;
                        const item = {
                          id: purchaseRequestItem.id,
                          purchase_request_item_id: purchaseRequestItem.id,
                          requested_quantity: requestedQuantity,
                          supplier_id: itemSupplier?.supplier?.id,
                          unit_price: unitPrice,
                          total_amount: totalAmount,
                          item_supplier_id: itemSupplier?.id
                        };

                        if (actionMeta.action == "select-option") {
                          if (
                            props.selectedItems.find((selectedItem) => selectedItem?.id == item.id && selectedItem?.supplier_id == item.supplier_id)
                          ) {
                            console.debug("Intentionally left empty");
                          } else if (
                            props.selectedItems.find((selectedItem) => selectedItem?.id == item.id && selectedItem?.supplier_id != item.supplier_id)
                          ) {
                            const foundIndex = props.selectedItems.findIndex(
                              (selectedItem) => selectedItem?.id == item.id && selectedItem?.supplier_id != item.supplier_id
                            );

                            if (foundIndex !== -1) {
                              props.selectedItems[foundIndex] = item;
                              setSelectedItems(props.selectedItems);
                            }
                          } else {
                            addToSelectedItems(item);
                          }
                          setValue(`data.${props.row.index}.total_amount`, totalAmount.toString());
                        } else {
                          removeFromSelectedItems(item);
                          setValue(`data.${props.row.index}.total_amount`, null);
                          setValue(`data.${props.row.index}.item_supplier_id`, "");
                        }
                      }}
                      theme={(theme) => ({
                        ...theme,
                        borderRadius: 7,
                        colors: {
                          ...theme.colors,
                          primary50: "#00897b50",
                          primary25: "#00897b25",
                          primary: "#00897b"
                        },
                        spacing: { baseUnit: 5.5 }
                      })}
                      value={
                        itemSuppliers.find(
                          (itemSupplier) =>
                            itemSupplier.id == field.value ||
                            props.selectedItems.find(
                              (selectedItem) => selectedItem?.id == purchaseRequestItem?.id && selectedItem?.item_supplier_id == itemSupplier.id
                            )
                        ) || ""
                      }
                    />
                  )}
                />
              </Col>
            </Row>
          );
        }
      },
      {
        Header: "Unit Price",
        accessor: "",
        Cell: (props) => {
          const isAggregate = props.row.canExpand;
          const unitPrice =
            watch(`data.${props.row.index}.unit_price`) || props.selectedItems.find((item) => item?.id === props.row.original?.id)?.unit_price;

          return isAggregate ? (
            ""
          ) : unitPrice ? (
            <div title={formatter.format(parseFloat(unitPrice))} data-bs-toggle="tooltip">
              <p className="text-right text-bold text-truncate">{formatter.format(parseFloat(unitPrice))}</p>
            </div>
          ) : (
            <p className="text-center">{"-"}</p>
          );
        }
      },
      {
        Header: "Total Amount",
        accessor: "",
        Aggregated: ({ row }) => {
          const leafRows = row.leafRows;
          const leafTotalAmounts = leafRows.map((leafRow) => {
            const totalAmount =
              watch(`data.${leafRow.id}.total_amount`) || +(getValues(`data.${leafRow.id}.unit_price`) || 0) * +leafRow.original.requested_quantity;
            return totalAmount ? totalAmount : "0.0";
          });
          const aggregatedTotal = aggregateSum(leafTotalAmounts);

          return aggregatedTotal ? (
            <div title={formatter.format(parseFloat(aggregatedTotal))} data-bs-toggle="tooltip">
              <p className="text-right text-bold text-truncate">{formatter.format(parseFloat(aggregatedTotal))}</p>
            </div>
          ) : (
            <p className="text-right text-bold text-truncate">{"₱0.0"}</p>
          );
        },
        Cell: (props) => {
          const isAggregate = props.row.canExpand;
          const totalAmount =
            watch(`data.${props.row.index}.total_amount`) || props.selectedItems.find((item) => item?.id === props.row.original?.id)?.total_amount;

          return isAggregate ? (
            ""
          ) : totalAmount ? (
            <div title={formatter.format(parseFloat(totalAmount))} data-bs-toggle="tooltip">
              <p className="text-right text-bold text-truncate">{formatter.format(parseFloat(totalAmount))}</p>
            </div>
          ) : (
            <p className="text-center">{"-"}</p>
          );
        }
      },
      {
        Header: "View P.O",
        accessor: "",
        aggregate: aggregateValue,
        Aggregated: ({ value }) => <p className="text-center text-bold">{value}</p>,
        Cell: (props) => {
          const purchaseOrders = props.row.original.purchase_orders;
          const hasPurchaseOrders = purchaseOrders?.length > 0;

          return (
            <div className="d-flex justify-content-center">
              <button
                className="button button--action button--action-success"
                title={hasPurchaseOrders ? "View Purchase Orders" : "No Purchase Orders"}
                data-bs-toggle="tooltip"
                onClick={() => {
                  setSelectedPurchaseRequest(props.row.original.purchase_request);
                  setShowPurchaseOrdersModal(!showPurchaseOrdersModal);
                  setPurchaseOrders(purchaseOrders);
                }}
              >
                <SvgIcon name="clipboard-list" className="h5" />
              </button>
            </div>
          );
        }
      }
    ],
    []
  );

  const getPurchaseRequestItems = React.useCallback(async ({ page, per, filters }) => {
    try {
      setIsLoading(true);
      // If there is a pending fetch request with associated AbortController, abort
      if (abortController.current) abortController.current.abort();
      // Assign a new AbortController for the latest fetch to our useRef variable
      abortController.current = new AbortController();
      const { signal } = abortController.current;

      const response = await api.get(admin_purchase_requests_path({ page, per, filters: JSON.stringify(filters) }), { signal });
      const data = response.data.data.filter(
        (purchase_request_item) => parseFloat(purchase_request_item.ordered_quantity) < parseFloat(purchase_request_item.requested_quantity)
      );
      setPurchaseRequestItems(
        data.map((d, i) => {
          setValue(`data.${i}.unit_price`, selectedItems.find((item) => item.id === d.id)?.unit_price);
          setValue(`data.${i}.total_amount`, selectedItems.find((item) => item.id === d.id)?.total_amount);
          return d;
        })
      );
      setPagination(response.data.pagination);
      setCurrentPage(response.data.pagination?.current || currentPage);
      return { pagination: response.data.pagination, data: response.data.data };
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  if (isForUser) {
    columns = React.useMemo(
      () => [
        {
          id: "id",
          Header: "Id",
          accessor: "id",
          Cell: () => {
            return <></>;
          }
        },
        {
          id: "code",
          Header: "Purchase Request",
          accessor: "code",
          Cell: (props) => {
            return <p className="text-center">{props.value}</p>;
          }
        },
        {
          id: "project_code",
          Header: "Project",
          accessor: "project.code",
          Cell: (props) => {
            return (
              <div title={props.value} data-bs-toggle="tooltip">
                <p className="text-truncate text-center">{props.value}</p>
              </div>
            );
          }
        },
        {
          id: "subdivision",
          Header: "Subdivision",
          accessor: "subdivision.name",
          Cell: (props) => {
            return (
              <div title={props.value} data-bs-toggle="tooltip">
                <p className="text-truncate text-center">{props.value}</p>
              </div>
            );
          }
        },
        {
          Header: "Requestor",
          accessor: "requested_by",
          Cell: (props) => {
            return (
              <div title={props.value} data-bs-toggle="tooltip">
                <p className="text-center text-truncate">{props.value}</p>
              </div>
            );
          }
        },
        {
          Header: "Approver",
          accessor: "approved_by",
          Cell: (props) => {
            return (
              <div title={props.value} data-bs-toggle="tooltip">
                <p className="text-center text-truncate">{props.value}</p>
              </div>
            );
          }
        },
        {
          Header: "Date Closed",
          accessor: "date_closed",
          Cell: (props) => {
            return (
              <div title={props.value} data-bs-toggle="tooltip">
                <p className="text-center text-truncate">{props.value ? moment(props.value).format("MM/DD/YYYY") : "-"}</p>
              </div>
            );
          }
        },
        {
          id: "status",
          Header: "Status",
          accessor: "status",
          Cell: (props) => {
            const status = I18n.t(`activerecord.attributes.purchase_request.statuses.${props.value}`);

            return (
              <div
                className={`d-flex justify-content-center badge badge--pill badge--${props.value.replace("_", "-")}`}
                title={status}
                data-bs-toggle="tooltip"
              >
                <p className="text-truncate">{status}</p>
              </div>
            );
          }
        },
        {
          Header: "Actions",
          accessor: "",
          Cell: (props) => {
            const { status, policy } = props.row.original;
            const isOpen = status == PurchaseRequestStatus.Open;
            const isForApproval = status == PurchaseRequestStatus.ForApproval;
            const hasPurchaseOrders = props.row.original.purchase_request_items?.some((item) => item.purchase_orders?.length > 0);

            return (
              <div className="d-flex justify-content-center">
                <a
                  className="button button--action button--action-success"
                  title="View Purchase Request"
                  data-bs-toggle="tooltip"
                  onClick={() => {
                    setPurchaseRequest(props.row.original);
                    setShowPurchaseRequestModal(!showPurchaseRequestModal);
                  }}
                >
                  <SvgIcon name="view" className="h5" />
                </a>
                {!isForApproval && (
                  <a
                    className="button button--action button--action-primary"
                    title="Print Purchase Request"
                    data-bs-toggle="tooltip"
                    onClick={() => {
                      window.open(print_admin_purchase_request_path(props.row.original.id));
                    }}
                  >
                    <SvgIcon name="printer" className="h5" />
                  </a>
                )}
                {isForUser && isForApproval && (
                  <>
                    {policy.approve_purchase_request && (
                      <a
                        className="button button--action button--action-approve"
                        title="Approve Purchase Request"
                        data-bs-toggle="tooltip"
                        onClick={() => {
                          setPurchaseRequest(props.row.original);
                          setShowApprovePurchaseRequestModal(!showApprovePurchaseRequestModal);
                        }}
                      >
                        <SvgIcon name="check-circle" className="h6" />
                      </a>
                    )}
                    {policy.reject_purchase_request && (
                      <a
                        className="button button--action button--action-danger"
                        title="Reject Purchase Request"
                        data-bs-toggle="tooltip"
                        onClick={() => {
                          setPurchaseRequest(props.row.original);
                          setShowRejectPurchaseRequestModal(!showRejectPurchaseRequestModal);
                        }}
                      >
                        <SvgIcon name="times-circle" className="h6" />
                      </a>
                    )}
                  </>
                )}
                {isForUser && (isOpen || isForApproval) && !hasPurchaseOrders && (
                  <a
                    className="button button--action button--action-danger"
                    title="Delete Purchase Request"
                    data-bs-toggle="tooltip"
                    onClick={() => {
                      setPurchaseRequest(props.row.original);
                      setShowDeletePurchaseRequestModal(!showDeletePurchaseRequestModal);
                    }}
                  >
                    <SvgIcon name="delete" className="h6" />
                  </a>
                )}
              </div>
            );
          }
        }
      ],
      []
    );
  }

  const purchaseOrderColumns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id",
        Cell: () => {
          return <></>;
        }
      },
      {
        Header: "Purchase Order",
        accessor: "code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Supplier",
        accessor: "supplier.name",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Ordered Quantity",
        accessor: "ordered_quantity",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: (props) => {
          const status = I18n.t(`activerecord.attributes.purchase_order.statuses.${props.value}`);

          return (
            <div
              className={`d-flex justify-content-center badge badge--pill badge--${props.value.replace("_", "-")}`}
              title={status}
              data-bs-toggle="tooltip"
            >
              <p className="text-truncate">{status}</p>
            </div>
          );
        }
      }
    ],
    []
  );

  const getPurchaseRequests = React.useCallback(async ({ page, per, filters }) => {
    try {
      setIsLoading(true);
      // If there is a pending fetch request with associated AbortController, abort
      if (abortController.current) abortController.current.abort();
      // Assign a new AbortController for the latest fetch to our useRef variable
      abortController.current = new AbortController();
      const { signal } = abortController.current;

      const response = await api.get(admin_purchase_requests_path({ is_for_user: true, page, per, filters: JSON.stringify(filters) }), { signal });
      setPurchaseRequests(response.data.data);
      setPagination(response.data.pagination);
      setCurrentPage(response.data.pagination?.current || currentPage);
      setHasPurchaseRequestHistory(response.data.has_purchase_request_history);
      setStatuses(response.data.statuses);
      return { pagination: response.data.pagination, data: response.data.data };
    } catch (error) {
      if (error?.message.includes("Cannot read properties of undefined (reading 'data')")) return;
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updatePurchaseRequests = (purchaseRequests, updatedPurchaseRequest) => {
    const updatedPurchaseRequests = purchaseRequests.map((purchaseRequest) => {
      if (purchaseRequest.id == updatedPurchaseRequest.id) {
        return { ...purchaseRequest, ...updatedPurchaseRequest };
      }

      return purchaseRequest;
    });

    setPurchaseRequests(updatedPurchaseRequests);
  };

  const approvePurchaseRequest = React.useCallback(async (purchaseRequests, purchaseRequestId, projectId) => {
    try {
      const response = await api.put(approve_purchase_request_admin_project_path({ id: projectId }, { purchase_request_id: purchaseRequestId }));
      updatePurchaseRequests(purchaseRequests, response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  }, []);

  const rejectPurchaseRequest = React.useCallback(async (purchaseRequests, purchaseRequestId, projectId) => {
    try {
      const response = await api.put(reject_purchase_request_admin_project_path({ id: projectId }, { purchase_request_id: purchaseRequestId }));
      updatePurchaseRequests(purchaseRequests, response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  }, []);

  const deletePurchaseRequest = React.useCallback(async (purchaseRequests, purchaseRequestId, projectId) => {
    try {
      const response = await api.deleteItem(
        delete_purchase_request_admin_project_path({ id: projectId }, { purchase_request_id: purchaseRequestId })
      );
      setPurchaseRequests(purchaseRequests.filter((purchaseRequest) => purchaseRequest.id != response.data.id));
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  }, []);

  const withItemsForDestruction = (data) => {
    const { purchase_request_items } = data;

    return { ...data, purchase_request_items: [...purchase_request_items, ...itemsForDestruction] };
  };

  const updatePurchaseRequest = async (data): Promise<unknown> => {
    try {
      await api.put(admin_purchase_request_path(data.id), {
        ...withItemsForDestruction(data)
      });
      await getPurchaseRequests({ page: 1, per: pagination?.limit || 10, filters: table.current.state.filters });
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setShowPurchaseRequestModal(!showPurchaseRequestModal);
    }
  };

  const onChange = useAsyncDebounce((column, value) => {
    table.current.setFilter(column, value);
  }, 400);

  const noContent = () => {
    return (
      <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
        <Row>
          <Col>
            <SvgIcon name="clipboard-list" className="display-icon text-gray" />
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>No available purchase requests for purchase order.</h5>
          </Col>
        </Row>
      </Container>
    );
  };

  const dataFilter = () => {
    if (isForUser) {
      return (
        <Row className="mb-3">
          <Col>
            <Input placeholder="Search for Purchase Request Number" onChange={(e) => onChange("code", e.target.value)} />
          </Col>
          <Col>
            <Input placeholder="Search for Project" onChange={(e) => onChange("project_code", e.target.value)} />
          </Col>
          <Col>
            <Input placeholder="Search for Subdivision" onChange={(e) => onChange("subdivision", e.target.value)} />
          </Col>
          <Col>
            <Select
              placeholder="Filter by Status"
              options={statuses}
              isSearchable={false}
              getOptionLabel={(option) => I18n.t(`activerecord.attributes.purchase_request.statuses.${option.value}`)}
              getOptionValue={(option) => option.value}
              onChange={(newOption) => {
                if (newOption) {
                  setStatusFilter(newOption.value);
                  table.current.setFilter("status", newOption.value);
                } else {
                  setStatusFilter("");
                  table.current.setFilter("status", "");
                }
              }}
              value={statuses.find((status) => status.value == statusFilter) || ""}
            />
          </Col>
        </Row>
      );
    }

    return (
      <Row className="mb-3" md={3} lg={4}>
        <Col className="mb-2">
          <Input placeholder="Search for Purchase Request Number" onChange={(e) => onChange("purchase_request_code", e.target.value)} />
        </Col>
        <Col className="mb-2">
          <Input placeholder="Search for Project ID" onChange={(e) => onChange("project_code", e.target.value)} />
        </Col>
        <Col className="mb-2">
          <Input placeholder="Search for Subdivision" onChange={(e) => onChange("subdivision", e.target.value)} />
        </Col>
        <Col className="mb-2">
          <Input placeholder="Search for Inventory ID" onChange={(e) => onChange("item_code", e.target.value)} />
        </Col>
        <Col className="mb-2">
          <Input placeholder="Search for Inventory Name" onChange={(e) => onChange("item_name", e.target.value)} />
        </Col>
        <Col className="mb-2">
          <Select
            placeholder="Filter by Warehouse"
            options={warehouses}
            isSearchable={false}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            isOptionDisabled={(option) => !option.active}
            onChange={(newOption) => {
              if (newOption) {
                setWarehouseFilter(newOption.id);
                table.current.setFilter("warehouse", newOption.id);
              } else {
                setWarehouseFilter("");
                table.current.setFilter("warehouse", "");
              }
            }}
            value={warehouses.find((warehouse) => warehouse.id == warehouseFilter) || ""}
          />
        </Col>
      </Row>
    );
  };

  const dataTable = () => {
    return (
      <Row>
        <Col>
          {isForUser ? (
            <ControlledTableContext.Provider value={{ currentPage, setCurrentPage }}>
              <ControlledTable
                columns={columns}
                data={purchaseRequests}
                ref={table}
                isLoading={isLoading}
                fetchData={getPurchaseRequests}
                totalCount={pagination?.total_count}
                totalPageCount={pagination?.total_pages || 0}
                previous={pagination?.previous}
                next={pagination?.next}
              />
            </ControlledTableContext.Provider>
          ) : (
            <ControlledTableContext.Provider value={{ currentPage, setCurrentPage }}>
              <ControlledTable
                columns={columns}
                data={purchaseRequestItems}
                ref={table}
                hiddenColumns={["purchase_request_code", "project_code", "subdivision", "warehouse"]}
                groupBy={["item_code"]}
                isLoading={isLoading}
                fetchData={getPurchaseRequestItems}
                totalCount={pagination?.total_count}
                totalPageCount={pagination?.total_pages || 0}
                previous={pagination?.previous}
                next={pagination?.next}
                selectedItems={selectedItems}
              />
            </ControlledTableContext.Provider>
          )}
        </Col>
      </Row>
    );
  };

  const content = () => {
    return has_purchase_request_items || isForUser ? (
      <>
        {dataFilter()}
        {dataTable()}
      </>
    ) : (
      noContent()
    );
  };

  React.useEffect(() => {
    const queryString = location.search;
    const urlParams = new URLSearchParams(queryString);
    const closed = urlParams.get("closed");
    const status = urlParams.get("status");

    if (closed && closed === "1") setTabIndex(1);
    if (status && status === "for_approval") {
      setStatusFilter(status);
      table.current.setFilter("status", status);
    }
  }, []);

  return (
    <React.StrictMode>
      {formErrors && formErrors.length != 0 && (
        <Alert>
          {formErrors.map((error, index) => {
            return <div key={index}>{error}</div>;
          })}
        </Alert>
      )}
      <Tabs
        className="card"
        selectedTabClassName="project-tab__link project-tab__link--active"
        selectedIndex={tabIndex}
        onSelect={(index: number) => setTabIndex(index)}
      >
        <Container fluid={true}>
          <Row className="d-flex align-items-baseline">
            <Col>
              <TabList className="card__header" style={{ padding: 0 }}>
                <Col className="d-flex justify-content-start">
                  <Tab className="project-tab__link">Purchase Requests</Tab>
                  <Tab className="project-tab__link">History</Tab>
                </Col>
              </TabList>
            </Col>
          </Row>
          <TabPanel>
            <Card.Header title="">
              {purchaseRequestItems.length > 0 && (
                <Button
                  className="button--brand"
                  icon="clipboard-list"
                  disabled={isSubmitting || isValidating}
                  title="Generate Purchase Orders"
                  type="button"
                  onClick={() => {
                    handleSubmit(async (data) => await save(data), onError)();
                  }}
                />
              )}
            </Card.Header>
            <Card.Body>{content()}</Card.Body>
          </TabPanel>
          <TabPanel>
            <PurchaseRequestHistory hasPurchaseRequestHistory={hasPurchaseRequestHistory} />
          </TabPanel>
        </Container>
      </Tabs>
      <Modal dialogClassName="modal-dialog-centered" show={showPurchaseOrdersModal}>
        <Modal.Header>{`View Purchase Orders for: ${selectedPurchaseRequest?.code}`}</Modal.Header>
        <Modal.Body>
          {purchaseOrders.length > 0 ? (
            <>
              <Table columns={purchaseOrderColumns} data={purchaseOrders} />
            </>
          ) : (
            <div className="text-center mt-4 mb-4">No available purchase orders.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowPurchaseOrdersModal(!showPurchaseOrdersModal);
            }}
            icon="times"
            title="Close"
          />
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered modal-xl" show={showPurchaseRequestModal}>
        <Modal.Header style={{ display: "block" }}>
          {isForUser ? (
            <>
              <p>{`Purchase Request: ${purchaseRequest?.code}`}</p>
              <p>{`Project ID: ${purchaseRequest?.project?.code}`}</p>
              <p>{`Requestor: ${purchaseRequest?.requested_by}`}</p>
            </>
          ) : (
            `Purchase Request: ${purchaseRequest?.code}`
          )}
        </Modal.Header>
        <Modal.Body>
          <ProjectPurchaseRequest
            purchaseRequest={purchaseRequest}
            control={control}
            reset={reset}
            getValues={getValues}
            setValue={setValue}
            setItemsForDestruction={setItemsForDestruction}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowPurchaseRequestModal(!showPurchaseRequestModal);
            }}
            icon="times"
            title="Close"
          />
          {[PurchaseRequestStatus.Open, PurchaseRequestStatus.ForApproval].includes(purchaseRequest?.status) &&
            purchaseRequest?.purchase_request_items?.some((item) => !(item.purchase_orders?.length > 0)) && (
              <Button
                className="button--brand"
                icon="save"
                title="Save"
                disabled={isSubmitting || isValidating}
                onClick={() => {
                  handleSubmit(async (data) => await updatePurchaseRequest(data), onError)();
                }}
              />
            )}
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showApprovePurchaseRequestModal}>
        <Modal.Header>{`Approve Purchase Request: ${purchaseRequest?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to approve purchase request: ${purchaseRequest?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowApprovePurchaseRequestModal(!showApprovePurchaseRequestModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--brand"
            icon="save"
            title="Yes, approve purchase request."
            onClick={() => {
              approvePurchaseRequest(purchaseRequests, purchaseRequest?.id, purchaseRequest?.project?.id).then(() => {
                setShowApprovePurchaseRequestModal(!showApprovePurchaseRequestModal);
              });
            }}
          />
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showRejectPurchaseRequestModal}>
        <Modal.Header>{`Reject Purchase Request: ${purchaseRequest?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to reject purchase request: ${purchaseRequest?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowRejectPurchaseRequestModal(!showRejectPurchaseRequestModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--danger"
            icon="times-circle"
            title="Yes, reject purchase request."
            onClick={() => {
              rejectPurchaseRequest(purchaseRequests, purchaseRequest?.id, purchaseRequest?.project?.id).then(() => {
                setShowRejectPurchaseRequestModal(!showRejectPurchaseRequestModal);
              });
            }}
          />
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showDeletePurchaseRequestModal}>
        <Modal.Header>{`Delete Purchase Request: ${purchaseRequest?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to delete purchase request: ${purchaseRequest?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowDeletePurchaseRequestModal(!showDeletePurchaseRequestModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--danger"
            icon="delete"
            title="Yes, delete purchase request."
            onClick={() => {
              deletePurchaseRequest(purchaseRequests, purchaseRequest?.id, purchaseRequest?.project?.id).then(() => {
                setShowDeletePurchaseRequestModal(!showDeletePurchaseRequestModal);
              });
            }}
          />
        </Modal.Footer>
      </Modal>
    </React.StrictMode>
  );
};

export default PurchaseRequests;
